import axios from 'axios';
import tokenService from './tokenService';

const API_URL = process.env.REACT_APP_API_URL;

// Create separate axios instances for different API groups
const authApi = axios.create({
  baseURL: `${API_URL}/auth`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const usersApi = axios.create({
  baseURL: `${API_URL}/users`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to automatically add auth header for user API
usersApi.interceptors.request.use((config) => {
  const token = tokenService.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class AuthService {
  
  // User login
  async login(email, password) {
    try {
      const response = await authApi.post('/login', {
        username: email,
        password
      });
      
      const { accessToken, refreshToken, user } = response.data;
      
      // Store tokens first
      tokenService.setTokens(accessToken, refreshToken);
      
      // Load user with permissions using getCurrentUser method
      const userWithPermissions = await this.getCurrentUser();
      
      return { success: true, user: userWithPermissions, accessToken };
    } catch (error) {
      console.error('Login error:', error);
      throw {
        message: error.response?.data?.error || 'Login failed',
        status: error.response?.status
      };
    }
  }

  // Manager registration (public)
  async register(userData) {
    try {
      const response = await authApi.post('/register', userData);
      
      return { 
        success: true, 
        message: response.data.message,
        user: response.data.user 
      };
    } catch (error) {
      console.error('Registration error:', error);
      throw {
        message: error.response?.data?.error || 'Registration failed',
        status: error.response?.status
      };
    }
  }

  // User logout
  async logout() {
    try {
      // Get current token for logout request
      const token = tokenService.getAccessToken();
      
      if (token) {
        await authApi.post('/logout', {}, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local logout even if server logout fails
    } finally {
      // Always clear local auth data
      tokenService.clearAuth();
    }
  }

  // Refresh access token
  async refreshToken() {
    try {
      const refreshToken = tokenService.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authApi.post('/refresh', {
        refreshToken
      });

      const { accessToken, refreshToken: newRefreshToken } = response.data;
      
      // Update stored tokens
      tokenService.setTokens(accessToken, newRefreshToken);
      
      return accessToken;
    } catch (error) {
      console.error('Token refresh error:', error);
      // If refresh fails, clear auth data and redirect to login
      tokenService.clearAuth();
      throw {
        message: error.response?.data?.error || 'Session expired',
        status: error.response?.status
      };
    }
  }

  // Get current user profile with permissions
  async getCurrentUser() {
    try {
      const token = tokenService.getAccessToken();
      
      if (!token) {
        throw new Error('No access token');
      }

      const response = await authApi.get('/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // The response structure is { success: true, user: {...}, permissions: {...} }
      // Extract the user object and permissions from the nested structure
      const userData = response.data.user;
      const permissionsData = response.data.permissions;
      
      // Handle location permissions for network/section-level users
      if (userData.permissionScope === 'network_section') {
        // Transform location permissions into accessible networks and sections arrays
        const accessibleNetworks = [];
        const accessibleSections = [];
        
        if (permissionsData?.locations) {
          permissionsData.locations.forEach(permission => {
            // Network-level permission (has network_id but no section_id)
            if (permission.network_id && !permission.section_id) {
              if (!accessibleNetworks.includes(permission.network_id)) {
                accessibleNetworks.push(permission.network_id);
              }
            }
            // Section-level permission (has section_id)
            else if (permission.section_id) {
              if (!accessibleSections.includes(permission.section_id)) {
                accessibleSections.push(permission.section_id);
              }
            }
          });
        }
        
        // Add accessible networks and sections to user data
        userData.accessible_networks = accessibleNetworks;
        userData.accessible_sections = accessibleSections;
        
        console.log(`🔐 [AuthService] Loaded location permissions for ${userData.username}:`, {
          networks: accessibleNetworks.length,
          sections: accessibleSections.length
        });
      } else {
        // Organization-level users have access to all organization resources
        userData.accessible_networks = [];
        userData.accessible_sections = [];
      }
      
      // Add all permissions (including disciplines) to user data
      userData.permissions = permissionsData || {};
      
      console.log(`🔐 [AuthService] Loaded discipline permissions for ${userData.username}:`, {
        disciplines: permissionsData?.disciplines?.length || 0,
        sub_disciplines: permissionsData?.sub_disciplines?.length || 0
      });
      
      // Store the user data with permissions
      tokenService.setUser(userData);
      
      return userData;
    } catch (error) {
      console.error('Get current user error:', error);
      
      // If token is invalid, try to refresh
      if (error.response?.status === 401) {
        try {
          await this.refreshToken();
          return await this.getCurrentUser();
        } catch (refreshError) {
          tokenService.clearAuth();
          throw refreshError;
        }
      }
      
      throw {
        message: error.response?.data?.error || 'Failed to get user profile',
        status: error.response?.status
      };
    }
  }

  // Forgot password
  async forgotPassword(email) {
    try {
      const response = await authApi.post('/forgot-password', { email });
      
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      throw {
        message: error.response?.data?.error || 'Failed to send reset email',
        status: error.response?.status
      };
    }
  }

  // Reset password
  async resetPassword(token, newPassword) {
    try {
      const response = await authApi.post('/reset-password', {
        token,
        newPassword
      });

      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Reset password error:', error);
      throw {
        message: error.response?.data?.error || 'Failed to reset password',
        status: error.response?.status
      };
    }
  }

  // Verify email
  async verifyEmail(token) {
    try {
      const response = await authApi.post('/verify-email', { token });
      
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Email verification error:', error);
      throw {
        message: error.response?.data?.error || 'Email verification failed',
        status: error.response?.status
      };
    }
  }

  // Get assignable resources (networks/sections for permission assignment)
  async getAssignableResources() {
    try {
      const response = await authApi.get('/assignable-resources', {
        headers: {
          'Authorization': `Bearer ${tokenService.getAccessToken()}`
        }
      });

      return {
        success: true,
        resources: response.data.resources
      };
    } catch (error) {
      console.error('Get assignable resources error:', error);
      throw {
        message: error.response?.data?.error || 'Failed to get assignable resources',
        status: error.response?.status
      };
    }
  }

  // Get users (for user management)
  async getUsers(isActive = true, filters = {}) {
    try {
      const params = new URLSearchParams();
      params.append('is_active', isActive); // Add is_active filter
      if (filters.organizationId) params.append('organization_id', filters.organizationId);
      if (filters.role) params.append('role', filters.role);
      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);

      const queryString = params.toString();
      const url = queryString ? `?${queryString}` : '';
      
      const response = await usersApi.get(url);

      return {
        success: true,
        users: response.data
      };
    } catch (error) {
      console.error('Get users error:', error);
      throw {
        message: error.response?.data?.error || 'Failed to get users',
        status: error.response?.status
      };
    }
  }

  // Get user permissions (for specific user)
  async getUserPermissions(userId) {
    try {
      const response = await usersApi.get(`/${userId}/permissions`);

      return {
        success: true,
        permissions: response.data.permissions
      };
    } catch (error) {
      console.error('Get user permissions error:', error);
      throw {
        message: error.response?.data?.error || 'Failed to get user permissions',
        status: error.response?.status
      };
    }
  }

  // Update user (edit user details, role, permissions)
  async updateUser(updateData) {
    try {
      const response = await usersApi.put(`/${updateData.userId}`, updateData);

      return {
        success: true,
        user: response.data,  // Backend returns user object directly, not wrapped
        message: 'User updated successfully'
      };
    } catch (error) {
      console.error('Update user error:', error);
      throw {
        message: error.response?.data?.error || 'Failed to update user',
        status: error.response?.status
      };
    }
  }

  // Create user (Manager creates Inspector/Viewer) - Updated method
  async createUser(userData) {
    try {
      const token = tokenService.getAccessToken();
      
      const response = await authApi.post('/create-user', userData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return {
        success: true,
        user: response.data.user,
        message: response.data.message,
        tempPassword: response.data.tempPassword,
        invitationToken: response.data.invitationToken
      };
    } catch (error) {
      console.error('Create user error:', error);
      throw {
        message: error.response?.data?.error || 'Failed to create user',
        status: error.response?.status
      };
    }
  }

  // Update user permissions
  async updateUserPermissions(userId, permissionData) {
    try {
      const token = tokenService.getAccessToken();
      
      const response = await authApi.put('/update-user-permissions', {
        userId,
        ...permissionData
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Update user permissions error:', error);
      throw {
        message: error.response?.data?.error || 'Failed to update user permissions',
        status: error.response?.status
      };
    }
  }

  // Update current user's profile
  async updateProfile(profileData) {
    try {
      const currentUser = tokenService.getUser();
      if (!currentUser) {
        throw new Error('No authenticated user found');
      }

      const updateData = {
        userId: currentUser.id,
        email: profileData.email,
        full_name: profileData.fullName
      };

      const response = await usersApi.put(`/${currentUser.id}`, updateData);

      // Update stored user data with the response
      const updatedUser = response.data;
      tokenService.setUser(updatedUser);

      return {
        success: true,
        user: updatedUser,
        message: 'Profile updated successfully'
      };
    } catch (error) {
      console.error('Update profile error:', error);
      throw {
        message: error.response?.data?.error || 'Failed to update profile',
        status: error.response?.status
      };
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return tokenService.isAuthenticated() && !tokenService.isTokenExpired();
  }

  // Get current user from storage (without API call)
  getCurrentUserFromStorage() {
    return tokenService.getUser();
  }

  // Check user role
  hasRole(role) {
    const user = tokenService.getUser();
    return user?.role === role;
  }

  // Check if user has any of the specified roles
  hasAnyRole(roles) {
    const user = tokenService.getUser();
    return roles.includes(user?.role);
  }

  // Check user permission scope
  hasPermissionScope(scope) {
    const user = tokenService.getUser();
    return user?.permissionScope === scope;
  }

  // Reset user password (Manager function)
  async resetUserPassword(resetData) {
    try {
      const response = await usersApi.put(`/${resetData.userId}/reset-password`, {
        newPassword: resetData.newPassword
      });
      
      return {
        success: true,
        message: response.data.message || 'Password reset successfully'
      };
    } catch (error) {
      console.error('Reset user password error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset user password'
      };
    }
  }

  // Delete user (Manager function)
  async deleteUser(userId) {
    try {
      const response = await usersApi.delete(`/${userId}`);
      
      return {
        success: true,
        message: response.data.message || 'User deleted successfully'
      };
    } catch (error) {
      console.error('Delete user error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete user'
      };
    }
  }

  // Reactivate user (Manager function)
  async reactivateUser(userId) {
    try {
      const response = await usersApi.put(`/${userId}/reactivate`);
      
      return {
        success: true,
        message: response.data.message || 'User reactivated successfully'
      };
    } catch (error) {
      console.error('Reactivate user error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reactivate user'
      };
    }
  }
}

export default new AuthService(); 